/* Page Invitation (/invitation/:id) — Factumis app.
   Permet à un utilisateur d'accepter ou de refuser une invitation à rejoindre
   un espace Factumis.

   Trois états principaux :
   1. Session en cours de chargement → spinner.
   2. Pas de session → carte explicative + liens vers /connexion et /inscription
      avec propagation du ?next= pour revenir ici après authentification.
   3. Session présente → appel getInvitation puis affichage de la carte
      d'invitation (accepter / refuser) ou messages d'erreur ciblés. */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { authClient, useSession } from '../lib/auth-client';
import { AuthLayout } from './AuthLayout';
import { Button } from '../components/ui';
import { Icon } from '../lib/icons';
import { roleLabel } from '../lib/roles';

/* Mappe les erreurs Better Auth vers un message FR utilisateur. */
function frenchAcceptMessage(error: { code?: string; status?: number }): string {
  if (
    error.code === 'SEAT_LIMIT_REACHED' ||
    error.code === 'EXTERNAL_ACCOUNTANT_LIMIT_REACHED' ||
    error.code === 'ORGANIZATION_MEMBERSHIP_LIMIT_REACHED'
  )
    return "L’espace a atteint son nombre maximal de membres.";
  return 'Une erreur est survenue, réessayez.';
}

type InvitationData = {
  organizationName: string;
  inviterEmail: string;
  role: string;
};

type LoadState =
  | { status: 'loading' }
  | { status: 'ok'; invitation: InvitationData }
  | { status: 'wrong-recipient' }
  | { status: 'expired' }
  | { status: 'error' };

type ActionState = 'idle' | 'accepting' | 'rejecting' | 'rejected';

export default function Invitation() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: session, isPending } = useSession();

  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading' });
  const [actionState, setActionState] = useState<ActionState>('idle');
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  /* Incrémenté par « Réessayer » pour relancer le chargement de l'invitation. */
  const [tick, setTick] = useState(0);

  /* Construire le paramètre next encodé pour les liens d'auth. */
  const nextEncoded = encodeURIComponent(`/invitation/${id}`);

  /* Charger l'invitation dès qu'une session est disponible. */
  useEffect(() => {
    if (!id || isPending || !session) return;

    setLoadState({ status: 'loading' });
    authClient.organization
      .getInvitation({ query: { id } })
      .then(({ data, error }) => {
        if (error) {
          if (error.code === 'YOU_ARE_NOT_THE_RECIPIENT_OF_THE_INVITATION') {
            setLoadState({ status: 'wrong-recipient' });
          } else if (error.status === 400 || error.status === 404) {
            /* Invitation introuvable, expirée, annulée ou déjà traitée. */
            setLoadState({ status: 'expired' });
          } else {
            /* Erreur réseau / serveur : proposer de réessayer. */
            setLoadState({ status: 'error' });
          }
          return;
        }
        if (!data) {
          setLoadState({ status: 'expired' });
          return;
        }
        setLoadState({
          status: 'ok',
          invitation: {
            organizationName: data.organizationName,
            inviterEmail: data.inviterEmail,
            role: data.role,
          },
        });
      })
      .catch(() => setLoadState({ status: 'error' }));
  }, [id, session, isPending, tick]);

  /* ------------------------------------------------------------------ */
  /* Handlers                                                             */
  /* ------------------------------------------------------------------ */

  async function handleAccept() {
    if (!id || actionState !== 'idle') return;
    setActionErr(null);
    setActionState('accepting');
    try {
      const { data, error } = await authClient.organization.acceptInvitation({ invitationId: id });
      if (error) {
        setActionState('idle');
        setActionErr(frenchAcceptMessage(error));
        return;
      }
      // Activer l'organisation que l'on vient de rejoindre avant de naviguer —
      // sinon l'utilisateur ayant déjà une org active atterrit sur la mauvaise.
      // Meilleur effort : une erreur setActive ne bloque pas la navigation
      // (RequireSession réconciliera la session côté client).
      const orgId = data?.invitation?.organizationId;
      if (orgId) {
        try {
          await authClient.organization.setActive({ organizationId: orgId });
        } catch {
          // ignore — non-bloquant
        }
      }
      navigate('/dashboard', { replace: true });
    } catch {
      setActionState('idle');
      setActionErr('Une erreur est survenue, réessayez.');
    }
  }

  async function handleReject() {
    if (!id || actionState !== 'idle') return;
    setActionErr(null);
    setActionState('rejecting');
    try {
      const { error } = await authClient.organization.rejectInvitation({ invitationId: id });
      if (error) {
        setActionState('idle');
        setActionErr('Une erreur est survenue, réessayez.');
        return;
      }
      setActionState('rejected');
    } catch {
      setActionState('idle');
      setActionErr('Une erreur est survenue, réessayez.');
    }
  }

  async function handleSignOut() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await authClient.signOut();
      /* La session disparaît → useSession retourne null → la branche
         « pas de session » s'affiche automatiquement. Pas de navigation
         supplémentaire nécessaire. */
    } finally {
      setSigningOut(false);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Branche 1 : session en cours de chargement                          */
  /* ------------------------------------------------------------------ */
  if (isPending) {
    return (
      <AuthLayout title="Invitation">
        <p className="auth-sub">Chargement…</p>
      </AuthLayout>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Branche 2 : pas de session                                          */
  /* ------------------------------------------------------------------ */
  if (!session) {
    return (
      <AuthLayout
        title="Invitation"
        subtitle="Vous avez été invité à rejoindre un espace Factumis. Connectez-vous ou créez un compte avec l'adresse email qui a reçu l'invitation."
      >
        <div className="auth-actions">
          <Link to={`/connexion?next=${nextEncoded}`} style={{ display: 'block' }}>
            <Button type="button" full>
              Se connecter
            </Button>
          </Link>
          <Link to={`/inscription?next=${nextEncoded}`} style={{ display: 'block' }}>
            <Button type="button" variant="soft" full>
              Créer un compte
            </Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Branche 3 : session présente — sous-états selon loadState           */
  /* ------------------------------------------------------------------ */

  /* 3a. Chargement de l'invitation */
  if (loadState.status === 'loading') {
    return (
      <AuthLayout title="Invitation">
        <p className="auth-sub">Chargement de l'invitation…</p>
      </AuthLayout>
    );
  }

  /* 3b. Mauvais destinataire */
  if (loadState.status === 'wrong-recipient') {
    return (
      <AuthLayout title="Invitation">
        <div className="auth-error" role="alert">
          <Icon name="alert" size={16} />
          Cette invitation est destinée à une autre adresse. Déconnectez-vous puis reconnectez-vous avec l'adresse invitée.
        </div>
        <div className="auth-actions">
          <Button type="button" full loading={signingOut} disabled={signingOut} onClick={handleSignOut}>
            Se déconnecter
          </Button>
        </div>
      </AuthLayout>
    );
  }

  /* 3c. Erreur de chargement (réseau, serveur) → réessayer */
  if (loadState.status === 'error') {
    return (
      <AuthLayout title="Invitation">
        <div className="auth-error" role="alert">
          <Icon name="alert" size={16} />
          Impossible de charger l'invitation.
        </div>
        <div className="auth-actions">
          <Button
            type="button"
            full
            onClick={() => {
              setLoadState({ status: 'loading' });
              setTick((t) => t + 1);
            }}
          >
            Réessayer
          </Button>
        </div>
      </AuthLayout>
    );
  }

  /* 3d. Expirée / déjà utilisée */
  if (loadState.status === 'expired') {
    return (
      <AuthLayout title="Invitation">
        <p className="auth-sub">Invitation expirée ou déjà utilisée.</p>
        <div className="auth-foot-row">
          <Link to="/connexion" className="auth-link">
            Aller à la connexion
          </Link>
        </div>
      </AuthLayout>
    );
  }

  /* 3e. Invitation refusée (état terminal) */
  if (actionState === 'rejected') {
    return (
      <AuthLayout title="Invitation">
        <p className="auth-sub">Invitation refusée.</p>
        <div className="auth-foot-row">
          <Link to="/connexion" className="auth-link">
            Aller à la connexion
          </Link>
        </div>
      </AuthLayout>
    );
  }

  /* 3f. Invitation valide — affichage principal */
  const { invitation } = loadState;
  const busy = actionState !== 'idle';

  return (
    <AuthLayout
      title="Rejoindre un espace"
      subtitle={`Vous avez été invité à rejoindre ${invitation.organizationName}.`}
    >
      {actionErr ? (
        <div className="auth-error" role="alert">
          <Icon name="alert" size={16} />
          {actionErr}
        </div>
      ) : null}

      <div style={{ marginTop: 'var(--sp-6)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        <p className="auth-sub" style={{ margin: 0 }}>
          <strong>Espace :</strong> {invitation.organizationName}
        </p>
        <p className="auth-sub" style={{ margin: 0 }}>
          <strong>Invité par :</strong> {invitation.inviterEmail}
        </p>
        <p className="auth-sub" style={{ margin: 0 }}>
          <strong>Rôle :</strong> {roleLabel(invitation.role)}
        </p>
      </div>

      <div className="auth-actions">
        <Button
          type="button"
          full
          loading={actionState === 'accepting'}
          disabled={busy}
          onClick={handleAccept}
        >
          Accepter l'invitation
        </Button>
        <Button
          type="button"
          variant="ghost"
          full
          loading={actionState === 'rejecting'}
          disabled={busy}
          onClick={handleReject}
        >
          Refuser
        </Button>
      </div>
    </AuthLayout>
  );
}
